<script setup>
import { computed, ref } from 'vue';
import DropdownContainer from 'next/dropdown-menu/base/DropdownContainer.vue';
import DropdownSection from 'next/dropdown-menu/base/DropdownSection.vue';
import DropdownBody from 'next/dropdown-menu/base/DropdownBody.vue';
import DropdownItem from 'next/dropdown-menu/base/DropdownItem.vue';
import { useFixedDropdown } from 'dashboard/composables/useFixedDropdown';

import Button from 'next/button/Button.vue';

// [{label, icon, value}]
const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  hideLabel: {
    type: Boolean,
    default: false,
  },
  hideIcon: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: 'faded',
  },
  fixed: {
    type: Boolean,
    default: false,
  },
});

const selected = defineModel({
  type: [String, Number],
  required: true,
});

const selectedOption = computed(() => {
  return props.options.find(o => o.value === selected.value) || {};
});

const iconToRender = computed(() => {
  if (props.hideIcon) return null;
  return selectedOption.value.icon || 'i-lucide-chevron-down';
});

const updateSelected = newValue => {
  selected.value = newValue;
};

const { buttonRef, buttonPosition, handleToggle } = useFixedDropdown(ref(props.fixed));
</script>

<template>
  <DropdownContainer>
    <template #trigger="{ toggle }">
      <slot name="trigger" :toggle="toggle">
        <Button
          ref="buttonRef"
          sm
          slate
          :variant
          :icon="iconToRender"
          :trailing-icon="selectedOption.icon ? false : true"
          :label="hideLabel ? null : selectedOption.label"
          @click="() => handleToggle(toggle)"
        />
      </slot>
    </template>
    <template v-if="fixed">
      <teleport to="body">
        <DropdownBody 
          class="min-w-48 z-50 click-outside-mark"
          strong
          :style="{
            position: 'fixed',
            top: `${buttonPosition.top}px`,
            left: `${buttonPosition.left}px`,
            width: `${buttonPosition.width}px`
          }"
        >
          <DropdownSection class="max-h-80 overflow-scroll">
            <DropdownItem
              v-for="option in options"
              :key="option.value"
              :label="option.label"
              :icon="option.icon"
              @click="updateSelected(option.value)"
            />
          </DropdownSection>
        </DropdownBody>
      </teleport>
    </template>
    <template v-else>
      <DropdownBody 
        class="top-0 min-w-48 z-50"
        strong
      >
        <DropdownSection class="max-h-80 overflow-scroll">
          <DropdownItem
            v-for="option in options"
            :key="option.value"
            :label="option.label"
            :icon="option.icon"
            @click="updateSelected(option.value)"
          />
        </DropdownSection>
      </DropdownBody>
    </template>
  </DropdownContainer>
</template>
